import { useTranslate } from "@similarweb/i18n-provider";
import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { TextAnchor, TextCell } from "@similarweb/pro-logic-components";
import { Type } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import appsSearchI18nKeys from "app/pages/sales-intelligence/modules/apps-search/utils/i18n";
import { appOsToStoreMap } from "app/pages/sales-intelligence/modules/apps-search-filters/constants";
import solutionUrlsService from "app/pages/sales-intelligence/services/solution-urls";

import type { CompanyAppItem } from "../../../types/rows";

import { BadgeWrapper, StyledItemIcon, StyledTooltip, StyledTooltipContent, TooltipTrigger } from "./styles";

type Props = {
    value?: readonly CompanyAppItem[];
};

const MARKET_BADGE_ICON: Record<CompanyAppItem["market"], IconName> = {
    android: "google-play-colored",
    ios: "apple-store-colored",
};

const MarketBadge = ({ market }: { market: CompanyAppItem["market"] }) => (
    <BadgeWrapper>
        <SWReactIcons
            iconName={MARKET_BADGE_ICON[market]}
            size="8"
        />
    </BadgeWrapper>
);

const CompanyAppsCell = ({ value }: Props) => {
    const { translate } = useTranslate();

    if (!value?.length) {
        return null;
    }

    const { key, replacements } = appsSearchI18nKeys.cell.companyAppsLabel(value.length);

    return (
        <StyledTooltip
            placement="right"
            content={
                <StyledTooltipContent>
                    {value.map((app) => (
                        <Flex
                            key={app.appId}
                            alignItems="center"
                            gap="8px"
                        >
                            <StyledItemIcon
                                iconType={Type.App}
                                iconSrc={app.icon}
                                badgeComponent={() => <MarketBadge market={app.market} />}
                            />

                            <TextAnchor
                                href={solutionUrlsService.getAppAnalysisUrl(
                                    app.appId,
                                    appOsToStoreMap[app.market],
                                )}
                                target="_blank"
                            >
                                {app.name}
                            </TextAnchor>
                        </Flex>
                    ))}
                </StyledTooltipContent>
            }
        >
            <TooltipTrigger data-automation="apps-search-company-apps-cell">
                <TextCell>{translate(key, replacements)}</TextCell>
            </TooltipTrigger>
        </StyledTooltip>
    );
};

export default CompanyAppsCell;
